import {
  Camera,
  CaretRight,
  Check,
  Database,
  Gear,
  Paperclip,
  Plus,
} from "@phosphor-icons/react";
import {
  Button,
  Flex,
  IconButton,
  Popover,
  Separator,
  Switch,
  Text,
} from "@radix-ui/themes";
import { useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAnalytics } from "../../../utils/analytics";
import { ACCEPTED_FILE_TYPES } from "../hooks/useFileDrop";

export interface ConnectorOption {
  id: string;
  label: string;
  icon: string;
  /** When true, connector is shown but greyed out (not connected) */
  disabled?: boolean;
}

const DEFAULT_TEST_ID_PREFIX = "ask-puntt";

interface InputPlusMenuProps {
  /** Size of the plus icon */
  iconSize?: number;
  /** Callback when files are selected */
  onFilesSelected?: (files: File[]) => void;
  /** Callback when a screenshot is captured */
  onScreenshotCaptured?: (file: File) => void;
  /** Enabled connectors state (controlled) */
  enabledConnectors?: Record<string, boolean>;
  /** Callback when connector is toggled */
  onConnectorToggle?: (connectorId: string, enabled: boolean) => void;
  /** When true, connector toggles are disabled (read-only, e.g. after chat creation) */
  connectorsDisabled?: boolean;
  /** Available connectors from enterprise settings. If empty/undefined, connector submenu is hidden. */
  availableConnectors?: ConnectorOption[];
  /**
   * Prefix every repeated child testid when a second plus menu is on screen
   * (home composer behind the Try walkthrough). Default keeps existing
   * `ask-puntt-*` ids.
   */
  testIdPrefix?: string;
  /** Disable the + trigger (walkthrough launching, read-only composers). */
  disabled?: boolean;
  /**
   * File patterns the picker offers, as `<input accept>` takes them. The
   * default is everything Ask Puntt takes; a warhol chat passes the shapes
   * warhol opens, so the picker never offers what the send would refuse.
   */
  accept?: string[];
}

export function InputPlusMenu({
  iconSize = 16,
  onFilesSelected,
  onScreenshotCaptured,
  enabledConnectors: controlledConnectors,
  onConnectorToggle,
  connectorsDisabled = false,
  availableConnectors = [],
  testIdPrefix = DEFAULT_TEST_ID_PREFIX,
  disabled = false,
  accept = ACCEPTED_FILE_TYPES,
}: InputPlusMenuProps) {
  const { t } = useTranslation("ui");
  const { t: tComponents } = useTranslation("components");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const analytics = useAnalytics();
  const [menuOpen, setMenuOpen] = useState(false);
  const [internalConnectors, setInternalConnectors] = useState<
    Record<string, boolean>
  >(() => {
    const initial: Record<string, boolean> = {};
    availableConnectors.forEach((c) => {
      initial[c.id] = !c.disabled;
    });
    return initial;
  });

  // Use controlled connectors if provided, otherwise use internal state
  const enabledConnectors = controlledConnectors ?? internalConnectors;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && onFilesSelected) {
      const fileArray = Array.from(files);
      fileArray.forEach((file) => {
        analytics.capture("ask_puntt_file_attached", {
          file_type: file.type || file.name.split(".").pop(),
          file_size: file.size,
          source: "file_picker",
        });
      });
      onFilesSelected(fileArray);
      setMenuOpen(false);
    }
    e.target.value = "";
  };

  const handleScreenshot = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: "browser" } as MediaTrackConstraints,
      });
      const video = document.createElement("video");
      video.srcObject = stream;
      await video.play();

      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(video, 0, 0);

      stream.getTracks().forEach((track) => track.stop());

      canvas.toBlob((blob) => {
        if (blob && onScreenshotCaptured) {
          const file = new File([blob], `screenshot-${Date.now()}.png`, {
            type: "image/png",
          });
          analytics.capture("ask_puntt_screenshot_taken");
          analytics.capture("ask_puntt_file_attached", {
            file_type: "image/png",
            file_size: blob.size,
            source: "screenshot",
          });
          onScreenshotCaptured(file);
          setMenuOpen(false);
        }
      }, "image/png");
    } catch (error) {
      console.error("Screenshot cancelled or failed:", error);
    }
  };

  const toggleConnector = (id: string) => {
    const connector = availableConnectors.find((c) => c.id === id);
    if (connectorsDisabled || connector?.disabled) return;
    const newEnabled = !enabledConnectors[id];
    analytics.capture("ask_puntt_connector_toggled", {
      connector: id,
      enabled: newEnabled,
    });
    if (onConnectorToggle) {
      onConnectorToggle(id, newEnabled);
    } else {
      setInternalConnectors((prev) => ({
        ...prev,
        [id]: !prev[id],
      }));
    }
  };

  const enabledSearchSources = availableConnectors.filter(
    (c) => !c.disabled && enabledConnectors[c.id],
  );

  return (
    <Popover.Root
      open={menuOpen}
      onOpenChange={(next) => {
        if (disabled) return;
        setMenuOpen(next);
      }}
    >
      <Popover.Trigger>
        <IconButton
          type="button"
          variant="ghost"
          color="gray"
          size="2"
          disabled={disabled}
          className="!m-0 cursor-pointer rounded-xl"
          data-testid={`${testIdPrefix}-attach-btn`}
          aria-label={t("addAttachmentOrContext")}
        >
          <Plus size={iconSize} />
        </IconButton>
      </Popover.Trigger>
      {/* Cap height, and scroll only the source list. With the Puntt DB row plus
          every enabled connector search source this menu grows taller than a short
          window, and Radix popovers are NOT page-scrollable: the bottom items
          (Connectors trigger, Manage) fell off-screen, and once the list scrolled,
          the menu opening upward put "Add files or photos" above the window's top
          edge (2026-10-28, a short window with eight connector sources). So the file and
          screenshot actions sit in a fixed head that never scrolls, the list below
          them scrolls, and the content keeps 8px from the window's edges. */}
      <Popover.Content
        side="top"
        align="start"
        collisionPadding={8}
        className="flex max-h-[min(70vh,420px)] w-[260px] flex-col overflow-hidden p-1"
        data-testid={`${testIdPrefix}-attach-menu`}
      >
        <div
          className="shrink-0"
          data-testid={`${testIdPrefix}-attach-menu-head`}
        >
          <input
            ref={fileInputRef}
            type="file"
            aria-label={t("addFilesOrPhotos")}
            data-testid={`${testIdPrefix}-attach-file-input`}
            className="hidden"
            accept={accept.join(",")}
            onChange={handleFileSelect}
            multiple
          />

          <Button
            type="button"
            variant="ghost"
            color="gray"
            size="2"
            className="w-full cursor-pointer justify-start py-2 pl-5"
            onClick={() => fileInputRef.current?.click()}
            data-testid={`${testIdPrefix}-add-files-btn`}
          >
            <Paperclip size={16} />
            {t("addFilesOrPhotos")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            color="gray"
            size="2"
            className="w-full cursor-pointer justify-start py-2 pl-5"
            onClick={handleScreenshot}
            data-testid={`${testIdPrefix}-screenshot-btn`}
          >
            <Camera size={16} />
            {t("takeScreenshot")}
          </Button>

          <Separator size="4" className="my-1" />
        </div>

        <div
          className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
          data-testid={`${testIdPrefix}-attach-menu-list`}
        >
          {/* Puntt database search - always shown */}
          <Flex align="center" gap="2" className="rounded-md px-3 py-2">
            <Database size={16} className="shrink-0 text-puntt-accent-9" />
            <Text size="2" className="flex-1 text-puntt-neutral-gray-12">
              {tComponents("askPuntt.inputPlusMenu.punttDatabaseSearch")}
            </Text>
            <Check size={14} className="text-puntt-accent-9" />
          </Flex>

          {/* Enabled connector search sources */}
          {enabledSearchSources.map((source) => (
            <Flex
              key={source.id}
              align="center"
              gap="2"
              className="rounded-md px-3 py-2"
            >
              <Text size="2" className="shrink-0">
                {source.icon}
              </Text>
              <Text size="2" className="flex-1 text-puntt-neutral-gray-12">
                {source.label}
              </Text>
              <Check size={14} className="text-puntt-accent-9" />
            </Flex>
          ))}

          <Separator size="4" className="my-1" />

          {/* Connectors - nested popover that opens to the right */}
          {availableConnectors.length > 0 && (
            <ConnectorsSubmenu
              connectors={availableConnectors}
              enabledConnectors={enabledConnectors}
              onToggle={toggleConnector}
              disabled={connectorsDisabled}
              testIdPrefix={testIdPrefix}
            />
          )}
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}

function ConnectorsSubmenu({
  connectors,
  enabledConnectors,
  onToggle,
  disabled = false,
  testIdPrefix = DEFAULT_TEST_ID_PREFIX,
}: {
  connectors: ConnectorOption[];
  enabledConnectors: Record<string, boolean>;
  onToggle: (id: string) => void;
  disabled?: boolean;
  testIdPrefix?: string;
}) {
  const { t } = useTranslation("ui");
  const { t: tComponents } = useTranslation("components");
  const [open, setOpen] = useState(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  const handleEnter = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    setOpen(true);
  };

  const handleLeave = () => {
    closeTimeout.current = setTimeout(() => setOpen(false), 200);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger>
        <button
          type="button"
          className="group flex w-full cursor-pointer items-center gap-2 rounded-md bg-base-white px-3 py-2 transition-colors hover:bg-puntt-accent-9"
          data-testid={`${testIdPrefix}-connectors-trigger`}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          onClick={() => setOpen((prev) => !prev)}
        >
          <Gear
            size={16}
            className="text-puntt-accent-9 group-hover:text-base-white"
          />
          <Text
            as="span"
            size="2"
            className="flex-1 text-left text-puntt-neutral-gray-12 group-hover:text-base-white"
          >
            {t("connectors")}
          </Text>
          <CaretRight
            size={14}
            className="group-hover:text-white text-puntt-neutral-gray-12"
          />
        </button>
      </Popover.Trigger>
      <Popover.Content
        side="right"
        align="start"
        sideOffset={8}
        className="max-h-[min(70vh,420px)] w-[260px] overflow-y-auto p-1"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        {connectors.map((connector) => (
          <Flex
            key={connector.id}
            align="center"
            gap="2"
            className={`rounded-md px-3 py-2 ${connector.disabled ? "opacity-50" : ""}`}
          >
            <Text size="2" className="shrink-0">
              {connector.icon}
            </Text>
            <Text
              size="2"
              className={`flex-1 ${connector.disabled ? "text-puntt-neutral-gray-9" : "text-puntt-neutral-gray-12"}`}
            >
              {connector.label}
              {connector.disabled && (
                <Text
                  as="span"
                  size="1"
                  className="ml-1 text-puntt-neutral-gray-8"
                >
                  {tComponents("askPuntt.inputPlusMenu.notConnected")}
                </Text>
              )}
            </Text>
            <Switch
              size="1"
              checked={!connector.disabled && !!enabledConnectors[connector.id]}
              onCheckedChange={() => onToggle(connector.id)}
              disabled={disabled || connector.disabled}
              data-testid={`${testIdPrefix}-connector-toggle`}
              className={
                disabled || connector.disabled
                  ? "cursor-not-allowed opacity-60"
                  : "cursor-pointer"
              }
            />
          </Flex>
        ))}

        <Separator size="4" className="my-1" />

        <Flex
          align="center"
          gap="2"
          className="cursor-pointer rounded-md px-3 py-2 hover:bg-puntt-neutral-gray-3"
          data-testid={`${testIdPrefix}-manage-connectors`}
          onClick={() => {
            setOpen(false);
            navigate({ to: "/integration-settings" });
          }}
        >
          <Gear size={16} className="text-puntt-neutral-gray-9" />
          <Text size="2" className="text-puntt-neutral-gray-11">
            {t("manageConnectors")}
          </Text>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
}
